import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import featureFlags from 'shared/lib/featureFlags';
import { fulltextPropTypes as jobPropTypes } from 'shared/proptypes/Job';
import { itemPropTypes } from 'shared/proptypes/AggregationItem';
import Pagination from 'shared/components/Pagination';
import JobSort from 'shared/components/JobSort';
import Card, { CardList } from 'shared/components/Card';
import { DEFAULT_CURRENCY } from 'shared/constants';
import { userportal as routes } from 'shared/routes';
import JobPreview from 'userportal/components/Job/Preview';
import NotFound from 'userportal/components/NotFound';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import styles from './List.module.scss';

const JobList = ({
  pageSize,
  total,
  loggedIn,
  jobs,
  currency,
  searchTitle,
  onRoleClick,
  onSkillClick,
  onEmploymentTypeClick,
  onCompanySizeClick,
  onCountryClick,
  onCityClick,
  onBenefitClick,
  onRateClick,
  withSeoLinks,
  searchItems,
  isLoading
}) => {
  const { t } = useTranslation('userportal');
  const navigate = useNavigate();

  const qs = queryString.parse(window.location.search);
  const handleSortChange = (newSort) => {
    const { sort, page, ...rest } = qs;
    if (newSort) {
      navigate(`?${queryString.stringify({ ...rest, sort: newSort })}`.replace(/%3A/g, ':'));
    } else {
      navigate(`?${queryString.stringify(rest)}`);
    }

    googleAnalytics.event('job_list_sort', { sort: newSort || 'default' });
  };

  return (
    <div className={styles.joblist}>
      {searchTitle && (
        <h1 className={total === 0 ? styles.nothingFoundTitle : null}>
          {searchTitle} - {t('jobs.suffix')}
        </h1>
      )}
      {total > 0 && (
        <div className={styles.header}>
          <JobSort
            fullWidth
            value={qs.sort}
            onChange={handleSortChange}
            loggedIn={loggedIn}
            withRelevancyOptions={featureFlags.search && searchItems.filter(({ not }) => !not).length > 1}
          />
        </div>
      )}

      {/* {!isBot && !loggedIn && searchItems.length === 0 && (
        <div className={styles.zamacore}>
          <a className={styles.logo} href="https://www.zamacore.cz" target="_blank" rel="noopener noreferrer">
            <img src="/images/zamacore-logo.png" alt="" />
          </a>
          <p>
            <a href="https://www.zamacore.cz" target="_blank" rel="noopener noreferrer">
              {t('adv.zamacore')}
            </a>
          </p>
        </div>
      )} */}

      {total === 0 && !isLoading && (
        <NotFound
          className="mt16"
          lines={[
            t('jobs.nothingFound.p1', { searchTitle }),
            loggedIn ? (
              <Trans i18nKey="userportal:jobs.nothingFound.p2user">
                <Link to={routes.user.profile.view} />
              </Trans>
            ) : (
              <Trans i18nKey="userportal:jobs.nothingFound.p2">
                <Link to={routes.signup} />
              </Trans>
            )
          ]}
          ctaTitle={t('jobs.nothingFound.cta')}
          ctaRoute="/"
        />
      )}

      <CardList className={styles.list}>
        {jobs.map(job => (
          <Card key={job.slug} noBorder spacingBottom="small">
            <JobPreview
              job={job}
              currency={currency}
              loggedIn={loggedIn}
              withSeoLinks={withSeoLinks}
              searchItems={searchItems}
              onRoleClick={onRoleClick}
              onSkillClick={onSkillClick}
              onEmploymentTypeClick={onEmploymentTypeClick}
              onCompanySizeClick={onCompanySizeClick}
              onCountryClick={onCountryClick}
              onCityClick={onCityClick}
              onBenefitClick={onBenefitClick}
              onRateClick={onRateClick}
            />
          </Card>
        ))}
      </CardList>
      {total > pageSize && (
        <Pagination pages={Math.ceil(total / pageSize)} />
      )}
    </div>
  );
};

JobList.propTypes = {
  total: PropTypes.number,
  pageSize: PropTypes.number,
  jobs: PropTypes.arrayOf(PropTypes.shape(jobPropTypes)),
  searchTitle: PropTypes.string,
  onRoleClick: PropTypes.func,
  onSkillClick: PropTypes.func,
  onEmploymentTypeClick: PropTypes.func,
  onCompanySizeClick: PropTypes.func,
  onCountryClick: PropTypes.func,
  onCityClick: PropTypes.func,
  onBenefitClick: PropTypes.func,
  onRateClick: PropTypes.func,
  loggedIn: PropTypes.bool,
  currency: PropTypes.string,
  withSeoLinks: PropTypes.bool,
  searchItems: PropTypes.arrayOf(PropTypes.shape(itemPropTypes)),
  isLoading: PropTypes.bool,
};

JobList.defaultProps = {
  total: 0,
  pageSize: 10,
  jobs: [],
  searchTitle: null,
  currency: DEFAULT_CURRENCY,
  onRoleClick() {},
  onSkillClick() {},
  onEmploymentTypeClick() {},
  onCompanySizeClick() {},
  onCountryClick() {},
  onCityClick() {},
  onBenefitClick() {},
  onRateClick() {},
  loggedIn: false,
  withSeoLinks: false,
  searchItems: [],
  isLoading: false,
};

export default JobList;
